/**
 * Catalog Panel — HMR-aware shell
 *
 * The Babylon.js Inspector captures side-pane component references at init time
 * and never re-requests them, so normal Vite HMR cannot swap the component.
 *
 * This file registers a thin wrapper with the inspector that uses Vite's
 * import.meta.hot API to accept updates from the real implementation module
 * (catalog-panel-impl.tsx). Edits to the impl file hot-reload instantly
 * without a full page reload.
 */

import React, { useState, useEffect, useRef } from 'react';
import type {
    ServiceDefinition,
    IShellService,
    SidePaneDefinition
} from '@babylonjs/inspector';
import { ShellServiceIdentity } from '@babylonjs/inspector';
import { CatalogPanelImpl } from './catalog-panel-impl';

// ============================================================================
// HMR Wrapper
// ============================================================================

const CatalogPanel: React.FC = () => {
    const [Impl, setImpl] = useState<React.FC>(() => CatalogPanelImpl);
    const counterRef = useRef(0);

    useEffect(() => {
        if (import.meta.hot) {
            import.meta.hot.accept('./catalog-panel-impl', (mod) => {
                const Comp = mod?.CatalogPanelImpl as React.FC | undefined;
                if (Comp) {
                    counterRef.current++;
                    setImpl(() => Comp);
                }
            });
        }
    }, []);

    // Key forces full remount on HMR so hooks reset cleanly
    return <Impl key={counterRef.current} />;
};

// ============================================================================
// Service Definition
// ============================================================================

const CatalogIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M1 2.5A1.5 1.5 0 012.5 1h3A1.5 1.5 0 017 2.5v3A1.5 1.5 0 015.5 7h-3A1.5 1.5 0 011 5.5v-3zm1.5-.5a.5.5 0 00-.5.5v3a.5.5 0 00.5.5h3a.5.5 0 00.5-.5v-3a.5.5 0 00-.5-.5h-3zM1 10.5A1.5 1.5 0 012.5 9h3A1.5 1.5 0 017 10.5v3A1.5 1.5 0 015.5 15h-3A1.5 1.5 0 011 13.5v-3zm1.5-.5a.5.5 0 00-.5.5v3a.5.5 0 00.5.5h3a.5.5 0 00.5-.5v-3a.5.5 0 00-.5-.5h-3zM9 2.5A1.5 1.5 0 0110.5 1h3A1.5 1.5 0 0115 2.5v3A1.5 1.5 0 0113.5 7h-3A1.5 1.5 0 019 5.5v-3zm1.5-.5a.5.5 0 00-.5.5v3a.5.5 0 00.5.5h3a.5.5 0 00.5-.5v-3a.5.5 0 00-.5-.5h-3zM9 10.5A1.5 1.5 0 0110.5 9h3a1.5 1.5 0 011.5 1.5v3a1.5 1.5 0 01-1.5 1.5h-3A1.5 1.5 0 019 13.5v-3zm1.5-.5a.5.5 0 00-.5.5v3a.5.5 0 00.5.5h3a.5.5 0 00.5-.5v-3a.5.5 0 00-.5-.5h-3z"/>
    </svg>
);

export const CatalogPanelServiceDefinition: ServiceDefinition<[], [IShellService]> = {
    friendlyName: 'Trace Studio Panel',

    consumes: [ShellServiceIdentity],

    factory: (shellService: IShellService) => {
        const paneDefinition: SidePaneDefinition = {
            key: 'catalog-panel',
            icon: CatalogIcon,
            content: CatalogPanel,
            horizontalLocation: 'right',
            verticalLocation: 'top',
            order: 3,
            title: 'Trace Studio'
        };

        const disposable = shellService.addSidePane(paneDefinition);

        return {
            dispose: () => {
                disposable.dispose();
            }
        };
    }
};
